import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import type { ComponentProps } from 'react';
import styles from './styles.module.scss';

/**
 * Radix UIのドロップダウンメニュー
 *
 * @see https://www.radix-ui.com/primitives/docs/components/dropdown-menu
 */
export const DropdownMenu = DropdownMenuPrimitive.Root;

export const DropdownMenuGroup = DropdownMenuPrimitive.Group;

type DropdownMenuTriggerProps = ComponentProps<typeof DropdownMenuPrimitive.Trigger>;

/**
 * ドロップダウンメニューを開くトリガー
 *
 * asChild（デフォルト: true）により、子要素にTriggerの機能を委譲します。
 * 子要素としてボタン要素（<button>など）を渡してください。
 *
 * 注意: asChild=trueの場合、子コンポーネントはrefを受け取れる必要があります。
 * - ネイティブHTML要素（<button>など）: そのまま使用可能
 * - カスタムコンポーネント: forwardRefでrefを転送する必要あり
 *
 * @example
 * ```tsx
 * // ネイティブbutton要素を使用
 * <DropdownMenuTrigger>
 *   <button>メニューを開く</button>
 * </DropdownMenuTrigger>
 *
 * // forwardRef対応のカスタムコンポーネントを使用
 * <DropdownMenuTrigger>
 *   <CustomButton>メニューを開く</CustomButton>
 * </DropdownMenuTrigger>
 * ```
 */
export const DropdownMenuTrigger = ({
  className,
  children,
  asChild = true,
  ...props
}: DropdownMenuTriggerProps) => (
  <DropdownMenuPrimitive.Trigger
    className={clsx(styles.trigger, className)}
    asChild={asChild}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.Trigger>
);

type DropdownMenuContentProps = ComponentProps<typeof DropdownMenuPrimitive.Content> & {
  /**
   * コンテンツをPortalでラップするかどうか
   * @default true
   */
  usePortal?: boolean;
};

export const DropdownMenuContent = ({
  className,
  children,
  sideOffset = 4,
  usePortal = true,
  ...props
}: DropdownMenuContentProps) => {
  const content = (
    <DropdownMenuPrimitive.Content
      className={clsx(styles.content, className)}
      sideOffset={sideOffset}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.Content>
  );

  if (usePortal) {
    return <DropdownMenuPrimitive.Portal>{content}</DropdownMenuPrimitive.Portal>;
  }

  return content;
};

type DropdownMenuLabelProps = ComponentProps<typeof DropdownMenuPrimitive.Label>;

export const DropdownMenuLabel = ({ className, ...props }: DropdownMenuLabelProps) => (
  <DropdownMenuPrimitive.Label className={clsx(styles.label, className)} {...props} />
);

type DropdownMenuItemProps = ComponentProps<typeof DropdownMenuPrimitive.Item>;

export const DropdownMenuItem = ({ className, ...props }: DropdownMenuItemProps) => (
  <DropdownMenuPrimitive.Item className={clsx(styles.item, className)} {...props} />
);

type DropdownMenuSeparatorProps = ComponentProps<typeof DropdownMenuPrimitive.Separator>;

export const DropdownMenuSeparator = ({ className, ...props }: DropdownMenuSeparatorProps) => (
  <DropdownMenuPrimitive.Separator className={clsx(styles.separator, className)} {...props} />
);
